import { DomainError } from '../errors/domain-error';
import {
  CreatorEventAccess,
  normalizeCreatorEventAccess,
  type CreatorEventAccessValue,
} from './event-access';
import { CreatorEventRsvpStatus, type CreatorEventRsvpStatusValue } from './event-satellite';

export const CreatorEventMeetingLinkReveal = {
  ALWAYS: 'always',
  GOING: 'going',
  START_WINDOW: 'start_window',
  TICKET: 'ticket',
} as const;

export type CreatorEventMeetingLinkRevealValue =
  (typeof CreatorEventMeetingLinkReveal)[keyof typeof CreatorEventMeetingLinkReveal];

export const CREATOR_EVENT_MEETING_LINK_REVEAL_VALUES = Object.values(
  CreatorEventMeetingLinkReveal,
);

export type EventEntitlementContext = {
  access: CreatorEventAccessValue | string;
  viewerAuthenticated: boolean;
  /** Circle membership entitled for linked perk (when applicable). */
  circleEntitled?: boolean;
  /** Tier gate passed when access is circle_tier. */
  tierEntitled?: boolean;
  /** Paid ticket recorded on RSVP. */
  ticketPaid?: boolean;
  /** Admin soft-takedown. */
  adminDisabled?: boolean;
  status?: string;
};

export type EventEntitlementResult = {
  canViewPage: boolean;
  canRsvp: boolean;
  entitled: boolean;
  reason:
    | 'ok'
    | 'admin_disabled'
    | 'cancelled'
    | 'auth_required'
    | 'circle_required'
    | 'tier_required'
    | 'ticket_required'
    | 'not_found';
};

/** Evaluate page view + RSVP entitlement (S04/S05). */
export function evaluateEventEntitlement(ctx: EventEntitlementContext): EventEntitlementResult {
  if (ctx.adminDisabled) {
    return {
      canViewPage: false,
      canRsvp: false,
      entitled: false,
      reason: 'admin_disabled',
    };
  }
  if (ctx.status === 'cancelled') {
    return {
      canViewPage: true,
      canRsvp: false,
      entitled: true,
      reason: 'cancelled',
    };
  }

  const access = normalizeCreatorEventAccess(ctx.access);

  switch (access) {
    case CreatorEventAccess.PUBLIC:
      return { canViewPage: true, canRsvp: true, entitled: true, reason: 'ok' };
    case CreatorEventAccess.REGISTERED:
      if (!ctx.viewerAuthenticated) {
        return {
          canViewPage: true,
          canRsvp: false,
          entitled: false,
          reason: 'auth_required',
        };
      }
      return { canViewPage: true, canRsvp: true, entitled: true, reason: 'ok' };
    case CreatorEventAccess.CIRCLE_MEMBERS:
      if (!ctx.circleEntitled) {
        return {
          canViewPage: false,
          canRsvp: false,
          entitled: false,
          reason: 'circle_required',
        };
      }
      return { canViewPage: true, canRsvp: true, entitled: true, reason: 'ok' };
    case CreatorEventAccess.CIRCLE_TIER:
      if (!ctx.circleEntitled || !ctx.tierEntitled) {
        return {
          canViewPage: false,
          canRsvp: false,
          entitled: false,
          reason: 'tier_required',
        };
      }
      return { canViewPage: true, canRsvp: true, entitled: true, reason: 'ok' };
    case CreatorEventAccess.TICKET_HOLDERS:
      if (!ctx.viewerAuthenticated) {
        return {
          canViewPage: true,
          canRsvp: false,
          entitled: false,
          reason: 'auth_required',
        };
      }
      if (!ctx.ticketPaid) {
        return {
          canViewPage: true,
          canRsvp: true,
          entitled: false,
          reason: 'ticket_required',
        };
      }
      return { canViewPage: true, canRsvp: true, entitled: true, reason: 'ok' };
    default:
      throw new DomainError(`Unsupported event access: ${access}`);
  }
}

export function shouldRevealMeetingLink(params: {
  reveal: CreatorEventMeetingLinkRevealValue | string | null | undefined;
  rsvpStatus?: CreatorEventRsvpStatusValue | string | null;
  ticketPaid?: boolean;
  startsAt?: Date | string | null;
  now?: Date;
}): boolean {
  const reveal = (params.reveal ?? CreatorEventMeetingLinkReveal.GOING) as string;
  if (reveal === CreatorEventMeetingLinkReveal.ALWAYS) return true;
  if (reveal === CreatorEventMeetingLinkReveal.TICKET) return Boolean(params.ticketPaid);
  if (reveal === CreatorEventMeetingLinkReveal.GOING) {
    return params.rsvpStatus === CreatorEventRsvpStatus.GOING;
  }
  if (reveal === CreatorEventMeetingLinkReveal.START_WINDOW) {
    if (!params.startsAt) return false;
    if (params.rsvpStatus !== CreatorEventRsvpStatus.GOING && !params.ticketPaid) return false;
    const start = new Date(params.startsAt).getTime();
    const now = (params.now ?? new Date()).getTime();
    const windowMs = 60 * 60 * 1000;
    return now >= start - windowMs;
  }
  return false;
}

/** Countdown helper for public event page (S04). */
export function eventCountdownParts(
  startsAt: Date | string | null | undefined,
  now: Date = new Date(),
): { totalMs: number; days: number; hours: number; minutes: number; seconds: number; started: boolean } | null {
  if (!startsAt) return null;
  const totalMs = new Date(startsAt).getTime() - now.getTime();
  if (totalMs <= 0) {
    return { totalMs: 0, days: 0, hours: 0, minutes: 0, seconds: 0, started: true };
  }
  const seconds = Math.floor(totalMs / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return { totalMs, days, hours, minutes, seconds: secs, started: false };
}

export function isCapacityFull(goingCount: number, capacity: number | null | undefined): boolean {
  if (capacity == null || capacity <= 0) return false;
  return goingCount >= capacity;
}

export function normalizeRsvpStatusInput(
  status: string | null | undefined,
): CreatorEventRsvpStatusValue {
  const value = (status ?? CreatorEventRsvpStatus.GOING).toLowerCase();
  const allowed = Object.values(CreatorEventRsvpStatus) as string[];
  if (!allowed.includes(value)) {
    throw new DomainError(`Unsupported RSVP status: ${status}`);
  }
  return value as CreatorEventRsvpStatusValue;
}

/** Counts toward capacity (S04/S06). */
export function rsvpCountsTowardCapacity(status: string): boolean {
  return status === CreatorEventRsvpStatus.GOING;
}

/** Build a minimal ICS VEVENT body (S11). */
export function buildEventIcs(params: {
  uid: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startsAt: Date | string;
  endsAt?: Date | string | null;
  timezone?: string | null;
  url?: string | null;
}): string {
  const fmt = (d: Date | string) => {
    const date = new Date(d);
    return date
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, 'Z');
  };
  const start = fmt(params.startsAt);
  const end = fmt(params.endsAt ?? new Date(new Date(params.startsAt).getTime() + 60 * 60 * 1000));
  const escape = (value: string) =>
    value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LOAMIFY//Events//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
    'BEGIN:VEVENT',
    `UID:${escape(params.uid)}`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escape(params.title)}`,
  ];
  if (params.description) lines.push(`DESCRIPTION:${escape(params.description)}`);
  if (params.location) lines.push(`LOCATION:${escape(params.location)}`);
  if (params.url) lines.push(`URL:${escape(params.url)}`);
  lines.push('END:VEVENT', 'END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

export type CreatorCalendarIcsEvent = {
  uid: string;
  title: string;
  description?: string | null;
  location?: string | null;
  startsAt: Date | string;
  endsAt?: Date | string | null;
  url?: string | null;
};

/** Build a VCALENDAR with multiple public events (L-E-E06 subscribe feed). */
export function buildCreatorEventsIcs(events: CreatorCalendarIcsEvent[]): string {
  const fmt = (d: Date | string) => {
    const date = new Date(d);
    return date
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, 'Z');
  };
  const escape = (value: string) =>
    value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,').replace(/\n/g, '\\n');
  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//LOAMIFY//CreatorEvents//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];
  for (const event of events) {
    const start = fmt(event.startsAt);
    const end = fmt(event.endsAt ?? new Date(new Date(event.startsAt).getTime() + 60 * 60 * 1000));
    lines.push(
      'BEGIN:VEVENT',
      `UID:${escape(event.uid)}`,
      `DTSTAMP:${fmt(new Date())}`,
      `DTSTART:${start}`,
      `DTEND:${end}`,
      `SUMMARY:${escape(event.title)}`,
    );
    if (event.description) lines.push(`DESCRIPTION:${escape(event.description)}`);
    if (event.location) lines.push(`LOCATION:${escape(event.location)}`);
    if (event.url) lines.push(`URL:${escape(event.url)}`);
    lines.push('END:VEVENT');
  }
  lines.push('END:VCALENDAR');
  return `${lines.join('\r\n')}\r\n`;
}

/** Append event page URL to ICS/GCal description when missing. */
export function withEventPageInDescription(
  description: string | null | undefined,
  url: string | null | undefined,
): string | null {
  if (!url?.trim()) return description?.trim() || null;
  const base = description?.trim() ?? '';
  if (base.includes(url)) return base || null;
  return base ? `${base}\n\n${url}` : url;
}

/** Google Calendar template deep link (S11). */
export function buildGoogleCalendarUrl(params: {
  title: string;
  description?: string | null;
  location?: string | null;
  startsAt: Date | string;
  endsAt?: Date | string | null;
}): string {
  const fmt = (d: Date | string) =>
    new Date(d)
      .toISOString()
      .replace(/[-:]/g, '')
      .replace(/\.\d{3}Z$/, 'Z');
  const start = fmt(params.startsAt);
  const end = fmt(params.endsAt ?? new Date(new Date(params.startsAt).getTime() + 60 * 60 * 1000));
  const q = new URLSearchParams({
    action: 'TEMPLATE',
    text: params.title,
    dates: `${start}/${end}`,
  });
  if (params.description) q.set('details', params.description);
  if (params.location) q.set('location', params.location);
  return `https://calendar.google.com/calendar/render?${q.toString()}`;
}

export const CREATOR_EVENT_RRULE_FREQ_VALUES = ['daily', 'weekly', 'monthly'] as const;
export type CreatorEventRruleFreqValue = (typeof CREATOR_EVENT_RRULE_FREQ_VALUES)[number];

export const EVENT_REGISTRATION_ANSWER_MAX_CHARS = 1000;
export const EVENT_REGISTRATION_QUESTIONS_MAX = 5;

export type EventRegistrationValidation = { ok: true } | { ok: false; message: string };

export function validateEventRegistrationAnswers(
  questions: Array<{ id: string; prompt: string; required: boolean }>,
  answers: Array<{ questionId: string; answer: string }> | undefined,
  maxAnswerChars = EVENT_REGISTRATION_ANSWER_MAX_CHARS,
): EventRegistrationValidation {
  if (questions.length === 0) return { ok: true };

  const answerByQuestion = new Map(
    (answers ?? []).map((entry) => [entry.questionId, entry.answer?.trim() ?? '']),
  );

  for (const question of questions) {
    const value = answerByQuestion.get(question.id) ?? '';
    if (question.required && !value) {
      return { ok: false, message: `Answer required: ${question.prompt}` };
    }
    if (value.length > maxAnswerChars) {
      return {
        ok: false,
        message: `Answer for "${question.prompt}" must be ${maxAnswerChars} characters or fewer`,
      };
    }
  }

  for (const entry of answers ?? []) {
    if (!questions.some((row) => row.id === entry.questionId)) {
      return { ok: false, message: 'Unknown registration question' };
    }
  }

  return { ok: true };
}

function addMonths(date: Date, months: number): Date {
  const next = new Date(date);
  next.setMonth(next.getMonth() + months);
  return next;
}

/** Expand daily|weekly|monthly RRULE materialization dates (S08 Lite). */
export function materializeSeriesOccurrences(params: {
  freq: CreatorEventRruleFreqValue;
  startsAt: Date;
  interval?: number;
  count?: number | null;
  until?: Date | null;
  max?: number;
}): Date[] {
  const interval = Math.max(1, params.interval ?? 1);
  const max = Math.min(params.max ?? 52, 104);
  const out: Date[] = [];
  let cursor = new Date(params.startsAt);
  const until = params.until ? params.until.getTime() : null;
  const count = params.count && params.count > 0 ? params.count : max;

  for (let i = 0; i < count && out.length < max; i += 1) {
    if (until != null && cursor.getTime() > until) break;
    out.push(new Date(cursor));
    if (params.freq === 'daily') {
      cursor = new Date(cursor.getTime() + interval * 24 * 60 * 60 * 1000);
    } else if (params.freq === 'weekly') {
      cursor = new Date(cursor.getTime() + interval * 7 * 24 * 60 * 60 * 1000);
    } else {
      cursor = addMonths(cursor, interval);
    }
  }
  return out;
}

/** @deprecated Use materializeSeriesOccurrences({ freq: 'weekly', ... }). */
export function materializeWeeklyOccurrences(params: {
  startsAt: Date;
  interval?: number;
  count?: number | null;
  until?: Date | null;
  max?: number;
}): Date[] {
  return materializeSeriesOccurrences({ ...params, freq: 'weekly' });
}
